import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableDropDown } from "@solsys/solsys-reactjs";
import { gql, useQuery } from "@apollo/client";

const gqlQuery = gql`
  query serviceDropDown($service_list_request: service_list_request) {
    service(service_list_request: $service_list_request) {
      edges {
        id
        fsr_id
        service_definition {
          id
          name
        }
        crane {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const mapServiceRow = (item) => ({
    ...item,
    ID: item.id,
    "Service Definition": item.service_definition?.name || "",
    name: item.service_definition?.name || `Service ${item.id}`,
});

const ServiceDropDown = ({ selected, ...props }) => {
    selected = selected === -1 ? undefined : selected;

    const truncateLength = 25;
    const craneFilterKey = String(props.craneId ?? "");

    const [requestState, setRequestState] = useState({
        page: 1,
        pageSize: 20,
        search: "",
    });
    const { page, pageSize, search } = requestState;

    const [accumulatedRaw, setAccumulatedRaw] = useState([]);
    const filtersKey = JSON.stringify(props.filters ?? []);
    const sortingKey = JSON.stringify(props.sorting ?? []);

    let variables = {
        service_list_request: {
            pagination: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
            },
            filters: [],
            sorting: [
                {
                    direction: "DESC",
                    field: "id",
                },
            ],
        },
    };

    let renderVariables = {
        service_list_request: {
            pagination: {
                limit: truncateLength,
                offset: 0,
            },
            filters: [],
            sorting: [
                {
                    direction: "DESC",
                    field: "id",
                },
            ],
        },
    };

    if (
        (Array.isArray(selected) && selected.length > 0) ||
        Number.isInteger(selected)
    ) {
        let array = [];
        if (props.multiple) {
            selected.forEach((v) => array.push(v));
        } else {
            array.push(selected);
        }
        renderVariables.service_list_request.filters.push({
            field: "id",
            operator: "in",
            value: `(${array.join(", ")})`,
        });
    }

    if (props.craneId) {
        variables.service_list_request.filters.push({
            field: "crane_id",
            operator: "=",
            value: String(props.craneId),
        });
    }

    if (search && search !== "")
        variables.service_list_request.filters.push({
            field: "service_definition.name",
            operator: "like",
            value: `%${search}%`,
        });
    if (props.filters?.length > 0) {
        props.filters.forEach((f) =>
            variables.service_list_request.filters.push(f)
        );
    }
    if (props.sorting?.length > 0) {
        props.sorting.forEach(
            (s) => (variables.service_list_request.sorting = [s])
        );
    }

    let { refetch: renderRefetch, ...renderObject } = useQuery(gqlQuery, {
        variables: renderVariables,
    });
    const renderLoading = renderObject.loading;
    const renderError = renderObject.error;
    const renderData = renderObject.data;

    useEffect(() => {
        renderRefetch();
    }, [selected, renderRefetch]);

    const renderEntityListResponse = renderData?.service;
    const renderEntityRaw = renderEntityListResponse?.edges ?? [];
    const renderEntityList = renderEntityRaw.map(mapServiceRow);

    const { refetch: refetchService, loading, error, data } = useQuery(gqlQuery, {
        variables,
    });

    useEffect(() => {
        if (!props.disabled) refetchService();
    }, [page, search, filtersKey, sortingKey, craneFilterKey, refetchService, props.disabled]);

    useEffect(() => {
        setAccumulatedRaw([]);
        setRequestState((s) => ({ ...s, page: 1 }));
    }, [search, filtersKey, sortingKey, craneFilterKey]);

    useEffect(() => {
        if (loading) return;
        const edges = data?.service?.edges;
        if (!edges) return;
        setAccumulatedRaw((prev) => {
            if (page === 1) return [...edges];
            const seen = new Set(prev.map((x) => Number(x.id)));
            const toAdd = edges.filter((x) => !seen.has(Number(x.id)));
            return [...prev, ...toAdd];
        });
    }, [data, page, loading]);

    const excludeSet = useMemo(
        () => new Set((props.excludeIds ?? []).map((id) => Number(id))),
        [props.excludeIds]
    );

    const totalCountRef = useRef(0);
    totalCountRef.current = data?.service?.totalCount ?? 0;
    const accumulatedLenRef = useRef(0);
    accumulatedLenRef.current = accumulatedRaw.length;

    const onSearch = (event) => {
        let { value } = event.target;
        setRequestState((s) => ({
            ...s,
            search: value,
            page: 1,
        }));
    };

    const paginationChange = (newPage) => {
        setRequestState((s) => ({
            ...s,
            page: newPage,
        }));
    };

    const addMoreResultsHandler = useCallback(() => {
        if (props.disabled) return;
        setRequestState((s) => {
            if (accumulatedLenRef.current >= totalCountRef.current) return s;
            return { ...s, page: s.page + 1 };
        });
    }, [props.disabled]);

    const entityList = accumulatedRaw
        .filter((item) => !excludeSet.has(Number(item.id)))
        .filter((item) => (props.onlyWithoutFsr ? item.fsr_id == null : true))
        .map(mapServiceRow);
    const entityCount = data?.service?.totalCount ?? 0;

    if (error) console.error(`Service drop down data error : ${error.message}`);
    if (renderError)
        console.error(`Service drop down render error : ${renderError.message}`);

    const headers = [
        { label: "ID" },
        { label: "Service Definition" }
    ];

    return (
        <TableDropDown
            { ...props }
            items={ entityList }
            loading={ loading }
            renderLoading={ renderLoading }
            multiple={ props.multiple }
            searchEnabled={ props.searchEnabled }
            searchValue={ search }
            onSearch={ onSearch }
            selectValue={ selected }
            selectRender={ renderEntityList }
            onSelect={ props.onSelect }
            dataKey={ "id" }
            identityColumnKey={ "ID" }
            renderColumnKey={ "name" }
            headers={ headers }
            truncateLength={ 25 }
            entityCount={ entityCount }
            onPaginationChange={ paginationChange }
            addMoreResults={
                typeof props.addMoreResults === "function"
                    ? props.addMoreResults
                    : addMoreResultsHandler
            }
        />
    );
};

export default ServiceDropDown;
